import {
  AbsoluteCenter,
  Box,
  FormControl,
  FormLabel,
  HStack,
  Input,
  ModalCloseButton,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import * as Yup from 'yup';

import {
  AnimatedLogo,
  CustomDrawerClosePrimaryButton,
  CustomFormErrorMessage,
  CustomModalActionPrimaryButton,
  CustomModalFooter,
  PrimaryButton,
  StatusBox,
} from '../../../../components/custom';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import {
  resetBillBeneficiaryUpdate,
  updateBillBeneficiaryAsync,
} from '../../../../store/slices/billPaymentBeneficiaries/updateBeneficiarySlice';
import { IBillBeneficiary } from '../../../../types/beneficiary';
import { getFormLabel } from './AddBeneficiaryForm';

interface DeleteBeneficiaryConfirmationProps {
  type?: string;
  billBeneficiary?: IBillBeneficiary;
}

export default function ViewEditBeneficiary({
  type,
  billBeneficiary,
}: DeleteBeneficiaryConfirmationProps) {
  const dispatch = useAppDispatch();

  const { status, value } = useAppSelector(
    (state) => state.updateBillBeneficiary
  );

  const initialValues = {
    alias: billBeneficiary?.billAlias ?? '',
  };

  const validationSchema = Yup.object().shape({
    alias: Yup.string(),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      // Do nothing
    },
  });

  const { errors, touched, values, getFieldProps } = formik;

  const { value: billCategoriesData } = useAppSelector(
    (state) => state.billerCategories
  );

  const billerNameToImageUrl: Record<string, string> = {};

  billCategoriesData?.forEach((category) => {
    if (category.billers) {
      category.billers.forEach((biller) => {
        billerNameToImageUrl[biller.name?.toLowerCase() || ''] =
          biller.imageUrl || '';
      });
    }
  });

  const handleEdit = () => {
    if (type === 'edit') {
      dispatch(
        updateBillBeneficiaryAsync({
          id: billBeneficiary?.id as string,
          customerId: billBeneficiary?.customerId as string,
          billerName: billBeneficiary?.billerName as string,
          billCategory: billBeneficiary?.billCategory as string,
          billerId: billBeneficiary?.billerId as string,
          billItemId: billBeneficiary?.billItemId as string,
          alias: values.alias as string,
        })
      );
    }
  };

  const alias = billBeneficiary?.billAlias ?? '';

  const message = value?.message ?? '';

  if (status === 'loading') {
    return (
      <Box
        minH="332px" // Totals to 500px
      >
        <AbsoluteCenter>
          <AnimatedLogo />
        </AbsoluteCenter>
      </Box>
    );
  }

  if (status === 'success') {
    return (
      <>
        <StatusBox
          variant="success"
          title={`${alias} beneficiary Updated successfully.`}
          px="0"
        >
          {message ?? ''}
        </StatusBox>

        <CustomDrawerClosePrimaryButton
          onClick={() => dispatch(resetBillBeneficiaryUpdate())}
        >
          Close
        </CustomDrawerClosePrimaryButton>
      </>
    );
  }

  if (status === 'failed') {
    return (
      <>
        <StatusBox
          variant="error"
          title={`Failed to update ${alias} beneficiary.`}
          px="0"
        >
          {message ?? `An error occurred while deleting ${alias} beneficiary`}
        </StatusBox>

        <CustomModalActionPrimaryButton
          onClick={() => {
            if (type === 'edit') {
              dispatch(resetBillBeneficiaryUpdate());
            }
          }}
        >
          Retry
        </CustomModalActionPrimaryButton>
      </>
    );
  }

  return (
    <Box m={0}>
      <Box mb={6}>
        <Text color="#000" mb={1} sx={{ textTransform: 'capitalize' }}>
          {type} Beneficiary
        </Text>
        <Text color="gray.300" fontSize=".875rem" lineHeight="171.5%">
          Lorem ipsum dolor sit amet, consectetur adipiscing elit.
        </Text>
      </Box>
      <Box pb={4}>
        <Stack
          gap={4}
          mb={8}
          sx={{
            borderRadius: '12px',
            padding: '16px',
            backgroundColor: '#FAFAFA',
          }}
        >
          <Text>Beneficiary Details</Text>
          <Stack gap={4}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Text
                sx={{
                  fontSize: '0.875rem',
                  color: 'richBlack.300',
                }}
              >
                {' '}
                Bill Type
              </Text>
              <Text
                sx={{
                  textTransform: 'capitalize',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {billBeneficiary?.billCategory?.toLowerCase()}
              </Text>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Text
                sx={{
                  fontSize: '0.875rem',
                  color: 'richBlack.300',
                }}
              >
                {' '}
                {
                  getFormLabel(
                    billBeneficiary?.billCategory?.toLowerCase() as string
                  )?.billerName
                }
              </Text>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '2px',
                }}
              >
                <Box>
                  <img
                    src={
                      billerNameToImageUrl[
                        billBeneficiary?.billerName?.toLowerCase() || ''
                      ] ||
                      'https://storage.googleapis.com/afl_public_bucket/afl-images/default.svg'
                    }
                    alt={billBeneficiary?.billerName || ''}
                    style={{
                      width: '27px',
                      height: '27px',
                    }}
                  />
                </Box>
                <Text
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                  }}
                >
                  {billBeneficiary?.billerName}
                </Text>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Text
                sx={{
                  fontSize: '0.875rem',
                  color: 'richBlack.300',
                }}
              >
                {
                  getFormLabel(
                    billBeneficiary?.billCategory?.toLowerCase() as string
                  )?.customerId
                }
              </Text>
              <Text
                sx={{
                  fontSize: '0.875rem',
                  fontWeight: 500,
                }}
              >
                {billBeneficiary?.customerId}
              </Text>
            </Box>
          </Stack>
        </Stack>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate>
            <FormControl
              id="alias"
              isInvalid={Boolean(touched.alias && errors.alias)}
            >
              <FormLabel
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Text>Beneficiary Alias </Text>{' '}
                <Text color="richBlack.200">Optional</Text>
              </FormLabel>
              <Input
                {...getFieldProps('alias')}
                disabled={type === 'view'}
                as="input"
                placeholder="Enter Beneficiary Alias"
                isInvalid={Boolean(touched.alias && errors.alias)}
              />
              <CustomFormErrorMessage>
                {touched.alias && errors.alias}
              </CustomFormErrorMessage>
            </FormControl>
          </Form>
        </FormikProvider>
      </Box>
      <CustomModalFooter>
        <HStack spacing={4}>
          {type === 'edit' ? (
            <PrimaryButton
              w="full"
              top="initial" // reset ModalCloseButton styles
              right="initial"
              flexShrink="initial"
              onClick={handleEdit}
            >
              Save Changes
            </PrimaryButton>
          ) : (
            <PrimaryButton
              w="full"
              as={ModalCloseButton}
              top="initial" // reset ModalCloseButton styles
              right="initial"
              flexShrink="initial"
            >
              Done
            </PrimaryButton>
          )}
        </HStack>
      </CustomModalFooter>
    </Box>
  );
}
